import { type ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { secondsFromHoursMinutes, splitSecondsToHoursMinutes } from '@/lib/format-duration';

interface LearningSessionSettings {
  maxAfkMinutes: number;
  maxSessionSeconds: number;
  maxDailySeconds: number;
  tickIntervalSeconds: number;
  pulseIntervalSeconds: number;
  timezone: string;
  updatedAt: string | null;
}

function NumberField({
  id,
  label,
  suffix,
  min,
  max,
  value,
  placeholder,
  onChange,
}: {
  id: string;
  label: string;
  suffix: string;
  min: number;
  max: number;
  value: number;
  placeholder?: string;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="lms-learning-session-settings__label">
        {label}
      </label>
      <div className="lms-learning-session-settings__input-wrap">
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          value={value}
          placeholder={placeholder}
          className="lms-learning-session-settings__input"
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <span className="lms-learning-session-settings__suffix" aria-hidden="true">
          {suffix}
        </span>
      </div>
    </div>
  );
}

function SettingsCard({
  icon,
  title,
  description,
  badgeIcon,
  badgeLabel,
  hint,
  children,
}: {
  icon: string;
  title: string;
  description: string;
  badgeIcon: string;
  badgeLabel: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="lms-learning-session-settings__card">
      <div className="lms-learning-session-settings__card-head">
        <div className="lms-learning-session-settings__card-intro">
          <div className="lms-learning-session-settings__icon" aria-hidden="true">
            <span className="material-symbols-outlined">{icon}</span>
          </div>
          <div>
            <h2 className="lms-learning-session-settings__card-title">{title}</h2>
            <p className="lms-learning-session-settings__card-desc">{description}</p>
          </div>
        </div>
        <span className="lms-learning-session-settings__badge">
          <span className="material-symbols-outlined" aria-hidden="true">
            {badgeIcon}
          </span>
          {badgeLabel}
        </span>
      </div>
      <div className="lms-learning-session-settings__card-body">
        {children}
        {hint ? <p className="lms-learning-session-settings__hint">{hint}</p> : null}
      </div>
    </section>
  );
}

export function LearningSessionSettingsPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.PROGRESO_ADMINISTRAR);
  const { data, isLoading, error } = useQuery({
    queryKey: ['learning-session-settings'],
    queryFn: () => api<LearningSessionSettings>('/admin/learning-session-settings'),
  });

  const [maxAfkMinutes, setMaxAfkMinutes] = useState(5);
  const [sessionHours, setSessionHours] = useState(4);
  const [sessionMinutes, setSessionMinutes] = useState(0);
  const [dailyHours, setDailyHours] = useState(8);
  const [dailyMinutes, setDailyMinutes] = useState(0);
  const [tickIntervalSeconds, setTickIntervalSeconds] = useState(15);
  const [pulseIntervalSeconds, setPulseIntervalSeconds] = useState(30);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const timezone = data?.timezone ?? 'America/Santiago';

  const applySettings = (settings: LearningSessionSettings) => {
    setMaxAfkMinutes(settings.maxAfkMinutes);
    const session = splitSecondsToHoursMinutes(settings.maxSessionSeconds);
    setSessionHours(session.hours);
    setSessionMinutes(session.minutes);
    const daily = splitSecondsToHoursMinutes(settings.maxDailySeconds);
    setDailyHours(daily.hours);
    setDailyMinutes(daily.minutes);
    setTickIntervalSeconds(settings.tickIntervalSeconds);
    setPulseIntervalSeconds(settings.pulseIntervalSeconds);
  };

  useEffect(() => {
    if (!data) return;
    applySettings(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api<LearningSessionSettings>('/admin/learning-session-settings', {
        method: 'PATCH',
        body: {
          maxAfkMinutes,
          maxSessionSeconds: secondsFromHoursMinutes(sessionHours, sessionMinutes),
          maxDailySeconds: secondsFromHoursMinutes(dailyHours, dailyMinutes),
          tickIntervalSeconds,
          pulseIntervalSeconds,
        },
      }),
    onSuccess: (saved) => {
      applySettings(saved);
      setSavedMessage('Configuración guardada.');
      queryClient.invalidateQueries({ queryKey: ['learning-session-settings'] });
      window.setTimeout(() => setSavedMessage(null), 3000);
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Cargando configuración…" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-600" role="alert">
        {(error as Error).message}
      </p>
    );
  }

  return (
    <div className="lms-learning-session-settings lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to="/administracion?tab=plugins" label="Volver a Administración" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Tiempo de sesión de aprendizaje</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Define cuándo deja de contarse el tiempo activo (AFK) y los topes por sesión y por día.
          </p>
        </div>
      </header>

      <form
        className="lms-learning-session-settings__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canManage) return;
          saveMutation.mutate();
        }}
      >
        <SettingsCard
          icon="timer_off"
          title="Tiempo máximo AFK"
          description="Sin interacción ni reproducción activa, el conteo se pausa tras este tiempo."
          badgeIcon="info"
          badgeLabel="Detección automática"
          hint="Despues de este tiempo, el tiempo activo se pausara."
        >
          <div className="lms-learning-session-settings__field-block">
            <NumberField
              id="afk_minutes"
              label="Minutos de inactividad permitidos"
              suffix="min"
              min={1}
              max={120}
              value={maxAfkMinutes}
              placeholder="15"
              onChange={setMaxAfkMinutes}
            />
          </div>
        </SettingsCard>

        <SettingsCard
          icon="sync"
          title="Registro por ticks"
          description="El cliente acumula tiempo cada tick y envía el total al servidor según el intervalo de envío."
          badgeIcon="info"
          badgeLabel="Sincronización cliente-servidor"
          hint="Frecuencia con la que el cliente mide actividad local y sincroniza los paquetes acumulados con el backend."
        >
          <div className="lms-learning-session-settings__field-grid">
            <NumberField
              id="tick_seconds"
              label="Tick (segundos)"
              suffix="seg"
              min={5}
              max={60}
              value={tickIntervalSeconds}
              placeholder="5"
              onChange={setTickIntervalSeconds}
            />
            <NumberField
              id="sync_seconds"
              label="Envío al servidor (segundos)"
              suffix="seg"
              min={10}
              max={120}
              value={pulseIntervalSeconds}
              placeholder="30"
              onChange={setPulseIntervalSeconds}
            />
          </div>
        </SettingsCard>

        <SettingsCard
          icon="history_toggle_off"
          title="Tiempo máximo por sesión"
          description="Tope de horas acumulables en una misma sesión del navegador, aunque el estudiante siga activo."
          badgeIcon="schedule"
          badgeLabel="Por conexión"
          hint="Al cumplirse este tiempo continuo, la sesión requerirá revalidación."
        >
          <div className="lms-learning-session-settings__field-grid">
            <NumberField
              id="session_hours"
              label="Horas"
              suffix="hrs"
              min={0}
              max={24}
              value={sessionHours}
              placeholder="0"
              onChange={setSessionHours}
            />
            <NumberField
              id="session_minutes"
              label="Minutos"
              suffix="min"
              min={0}
              max={59}
              value={sessionMinutes}
              placeholder="0"
              onChange={setSessionMinutes}
            />
          </div>
        </SettingsCard>

        <SettingsCard
          icon="calendar_today"
          title="Tiempo máximo por día"
          description={`Tope diario de horas acumulables por estudiante (zona horaria: ${timezone}).`}
          badgeIcon="info"
          badgeLabel={`Zona horaria: ${timezone}`}
          hint="Al alcanzarse este tope diario, el progreso se suspenderá hasta el día siguiente."
        >
          <div className="lms-learning-session-settings__field-grid">
            <NumberField
              id="daily_hours"
              label="Horas"
              suffix="hrs"
              min={0}
              max={24}
              value={dailyHours}
              placeholder="8"
              onChange={setDailyHours}
            />
            <NumberField
              id="daily_minutes"
              label="Minutos"
              suffix="min"
              min={0}
              max={59}
              value={dailyMinutes}
              placeholder="0"
              onChange={setDailyMinutes}
            />
          </div>
        </SettingsCard>

        <div className="lms-learning-session-settings__actions">
          <div className="lms-learning-session-settings__actions-group">
            <button
              type="button"
              className="lms-learning-session-settings__reset"
              onClick={() => {
                if (data) applySettings(data);
              }}
            >
              Restablecer valores
            </button>
            {canManage ? (
              <button
                type="submit"
                className="lms-learning-session-settings__save"
                disabled={saveMutation.isPending}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  save
                </span>
                {saveMutation.isPending ? 'Guardando…' : 'Guardar configuración'}
              </button>
            ) : null}
          </div>
          {savedMessage ? <p className="lms-learning-session-settings__saved">{savedMessage}</p> : null}
          {saveMutation.error ? (
            <p className="lms-learning-session-settings__error" role="alert">
              {(saveMutation.error as Error).message}
            </p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
